import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store, select } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { IndividualConfig, ToastrService } from 'ngx-toastr';
import { EMPTY, OperatorFunction, Subject, combineLatest } from 'rxjs';
import { concatMap, map, mergeMap, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { OrderTemplateService } from 'src/app/extensions/order-templates/services/order-template/order-template.service';

import { BasketService } from 'ish-core/services/basket/basket.service';
import { getDeviceType } from 'ish-core/store/core/configuration';
import { MessagesPayloadType } from 'ish-core/store/core/messages';
import { getHeaderHeight, isStickyHeader } from 'ish-core/store/core/viewconf';
import {
  addItemsToBasketSuccess,
  createBasketFail,
  createBasketSuccess,
  getCurrentBasketId,
} from 'ish-core/store/customer/basket';
import { DomService } from 'ish-core/utils/dom/dom.service';
import { mapErrorToAction, mapToPayload, mapToPayloadProperty } from 'ish-core/utils/operators';

import { CustomQuickorderService } from '../../services/custom-quickorder/custom-quickorder.service';

import {
  addQuickOrderFlyOutItemsToBasket,
  addQuickOrderFlyOutItemsToBasketFail,
  addQuickOrderFlyOutItemsToBasketSuccess,
  addQuickOrderItemsToBasket,
  addQuickOrderItemsToBasketFail,
  addQuickOrderItemsToBasketSuccess,
  createQuickOrderFlyoutItems,
  createQuickOrderFlyoutItemsFail,
  createQuickOrderFlyoutItemsSuccess,
  createQuickOrderItems,
  createQuickOrderItemsFail,
  createQuickOrderItemsSuccess,
  createQuickOrderUploadItems,
  createQuickOrderUploadItemsFail,
  createQuickOrderUploadItemsSuccess,
  deleteQuickOrder,
  deleteQuickOrderFail,
  deleteQuickOrderItems,
  deleteQuickOrderItemsFail,
  deleteQuickOrderItemsSuccess,
  deleteQuickOrderSuccess,
  loadQuickOrder,
  loadQuickOrderFail,
  loadQuickOrderSuccess,
  showQuickOrderDeleteItemsSuccessMessage,
  showQuickOrderDeleteItemsWarningMessage,
  showQuickOrderUploadItemsSuccessMessage,
  showQuickOrderUploadItemsWarningMessage,
  updateQuickOrderItem,
  updateQuickOrderItemFail,
  updateQuickOrderItemSuccess,
} from './quick-order.actions';
import { getQuickOrderID, getQuickOrderItemsCount, getQuickOrderValidations } from './quick-order.selectors';

@Injectable()
export class QuickOrderEffects {
  constructor(
    private actions$: Actions,
    private quickOrderService: CustomQuickorderService,
    private router: Router,
    private store: Store,
    private basketService: BasketService,
    private orderTemplateService: OrderTemplateService,
    private toastr: ToastrService,
    private translate: TranslateService,
    private domService: DomService
  ) {}
  private applyStyle$ = new Subject<void>();

  loadQuickOrder$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadQuickOrder),
      concatMap(() =>
        this.quickOrderService.getCustomQuickorder().pipe(
          map(quickOrder => loadQuickOrderSuccess({ quickOrder })),
          mapErrorToAction(loadQuickOrderFail)
        )
      )
    )
  );

  createQuickOrderItems$ = createEffect(() =>
    this.actions$.pipe(
      ofType(createQuickOrderItems),
      mapToPayloadProperty('quickOrderItems'),
      concatMap(quickOrderItems =>
        this.quickOrderService.createCustomQuickorder(quickOrderItems).pipe(
          map(quickOrder => createQuickOrderItemsSuccess({ quickOrder })),
          mapErrorToAction(createQuickOrderItemsFail)
        )
      )
    )
  );

  createQuickOrderFlyoutItems$ = createEffect(() =>
    this.actions$.pipe(
      ofType(createQuickOrderFlyoutItems),
      mapToPayloadProperty('quickOrderItems'),
      concatMap(quickOrderItems =>
        this.quickOrderService.createCustomQuickorder(quickOrderItems).pipe(
          map(quickOrder => createQuickOrderFlyoutItemsSuccess({ quickOrder })),
          mapErrorToAction(createQuickOrderFlyoutItemsFail)
        )
      )
    )
  );

  createQuickOrderUploadItems$ = createEffect(() =>
    this.actions$.pipe(
      ofType(createQuickOrderUploadItems),
      mapToPayloadProperty('quickOrderItems'),
      concatMap(quickOrderItems =>
        this.quickOrderService.createCustomQuickorder(quickOrderItems).pipe(
          map(quickOrder => createQuickOrderUploadItemsSuccess({ quickOrder })),
          mapErrorToAction(createQuickOrderUploadItemsFail)
        )
      )
    )
  );

  showQuickOrderUploadItemsMessage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(createQuickOrderUploadItemsSuccess),
      withLatestFrom(this.store.pipe(select(getQuickOrderValidations))),
      map(([_, quickOrderErrors]) => {
        if (quickOrderErrors?.length > 0) return showQuickOrderUploadItemsWarningMessage();
        else return showQuickOrderUploadItemsSuccessMessage();
      })
    )
  );

  showQuickOrderUploadItemsWarningMessage$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(showQuickOrderUploadItemsWarningMessage),

        map(
          (): MessagesPayloadType => ({
            message: 'dias.quickorder.page.upload.warning.message',
          })
        ),
        this.composeQuickOrderToastArguments(),
        map(args => this.toastr.warning(...args)),
        tap(() => {
          this.applyStyle$.next();
        })
      ),
    { dispatch: false }
  );

  showQuickOrderUploadItemsSuccessMessage$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(showQuickOrderUploadItemsSuccessMessage),

        map(
          (): MessagesPayloadType => ({
            message: 'dias.quickorder.page.upload.success.message',
          })
        ),
        this.composeQuickOrderToastArguments(),
        map(args => this.toastr.success(...args)),
        tap(() => {
          this.applyStyle$.next();
        })
      ),
    { dispatch: false }
  );

  updateQuickOrderItem$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateQuickOrderItem),
      mapToPayload(),
      concatMap(payload =>
        this.quickOrderService.updateCustomQuickorder(payload.itemId, payload.quickOrderItem).pipe(
          map(quickOrder => updateQuickOrderItemSuccess({ quickOrder })),
          mapErrorToAction(updateQuickOrderItemFail)
        )
      )
    )
  );

  deleteQuickOrderItems$ = createEffect(() =>
    this.actions$.pipe(
      ofType(deleteQuickOrderItems),
      mapToPayloadProperty('itemIds'),
      concatMap(itemIds =>
        this.quickOrderService.deleteCustomQuickorder(itemIds).pipe(
          map(quickOrder => deleteQuickOrderItemsSuccess({ quickOrder })),
          mapErrorToAction(deleteQuickOrderItemsFail)
        )
      )
    )
  );

  showDeleteQuickOrderItemsMessage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(deleteQuickOrderItemsSuccess),
      withLatestFrom(this.store.pipe(select(getQuickOrderItemsCount))),
      map(([_, quickOrderItems]) => {
        if (quickOrderItems > 0) return showQuickOrderDeleteItemsWarningMessage();
        else return showQuickOrderDeleteItemsSuccessMessage();
      })
    )
  );
  showQuickOrderDeleteItemsWarningMessage$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(showQuickOrderDeleteItemsWarningMessage),

        map(
          (): MessagesPayloadType => ({
            message: 'dias.quickorder.page.delete.warning.message',
          })
        ),
        this.composeQuickOrderToastArguments(),
        map(args => this.toastr.warning(...args)),
        tap(() => {
          this.applyStyle$.next();
        })
      ),
    { dispatch: false }
  );

  showQuickOrderDeleteItemsSuccessMessage$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(showQuickOrderDeleteItemsSuccessMessage),

        map(
          (): MessagesPayloadType => ({
            message: 'dias.quickorder.page.delete.all.success.message',
          })
        ),
        this.composeQuickOrderToastArguments(),
        map(args => this.toastr.success(...args)),
        tap(() => {
          this.applyStyle$.next();
        })
      ),
    { dispatch: false }
  );

  navigateToQuickOrderPage$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(createQuickOrderFlyoutItemsSuccess),
        concatMap(() => {
          if (!this.router.url.includes('custom-quickorder')) this.router.navigate(['custom-quickorder']);
          return EMPTY;
        })
      ),
    { dispatch: false }
  );

  // resetQuickOrderState$ = createEffect(() =>
  //   this.actions$.pipe(
  //     ofType(loginUserSuccess, logoutUserSuccess),
  //     map(() => resetQuickOrderState())
  //   )
  // );

  addQuickOrderFlyOutItemsToBasket$ = createEffect(() =>
    this.actions$.pipe(
      ofType(addQuickOrderFlyOutItemsToBasket),
      mapToPayload(),
      withLatestFrom(this.store.pipe(select(getCurrentBasketId))),
      concatMap(([{ items }, basketId]) => {
        if (basketId) {
          return this.basketService.addItemsToBasket(items).pipe(
            mergeMap(payload => [addItemsToBasketSuccess(payload), addQuickOrderFlyOutItemsToBasketSuccess()]),
            mapErrorToAction(addQuickOrderFlyOutItemsToBasketFail)
          );
        } else {
          return this.basketService.createBasket().pipe(
            switchMap(basket =>
              this.basketService.addItemsToBasket(items).pipe(
                mergeMap(payload => [
                  createBasketSuccess({ basket }),
                  addItemsToBasketSuccess(payload),
                  addQuickOrderFlyOutItemsToBasketSuccess(),
                ]),
                mapErrorToAction(addQuickOrderFlyOutItemsToBasketFail)
              )
            ),
            mapErrorToAction(createBasketFail)
          );
        }
      })
    )
  );

  addQuickOrderItemsToBasket$ = createEffect(() =>
    this.actions$.pipe(
      ofType(addQuickOrderItemsToBasket),
      mapToPayload(),
      withLatestFrom(this.store.pipe(select(getCurrentBasketId))),
      concatMap(([{ items }, basketId]) => {
        if (basketId) {
          return this.basketService.addItemsToBasket(items).pipe(
            mergeMap(payload => [addItemsToBasketSuccess(payload), addQuickOrderItemsToBasketSuccess()]),
            mapErrorToAction(addQuickOrderItemsToBasketFail)
          );
        } else {
          return this.basketService.createBasket().pipe(
            switchMap(basket =>
              this.basketService.addItemsToBasket(items).pipe(
                mergeMap(payload => [
                  createBasketSuccess({ basket }),
                  addItemsToBasketSuccess(payload),
                  addQuickOrderItemsToBasketSuccess(),
                ]),
                mapErrorToAction(addQuickOrderItemsToBasketFail)
              )
            ),
            mapErrorToAction(createBasketFail)
          );
        }
      })
    )
  );

  dispatchDeleteQuickOrder$ = createEffect(() =>
    this.actions$.pipe(
      ofType(addQuickOrderItemsToBasketSuccess),
      withLatestFrom(this.store.pipe(select(getQuickOrderID))),
      map(([_, quickOrderID]) => deleteQuickOrder({ orderTemplateId: quickOrderID }))
    )
  );

  deleteQuickOrder$ = createEffect(() =>
    this.actions$.pipe(
      ofType(deleteQuickOrder),
      mapToPayloadProperty('orderTemplateId'),
      mergeMap(quickOrderID =>
        this.orderTemplateService.deleteOrderTemplate(quickOrderID).pipe(
          map(() => deleteQuickOrderSuccess()),
          mapErrorToAction(deleteQuickOrderFail)
        )
      )
    )
  );

  loadNewQuickOrderAfterAddingItemsToBasket$ = createEffect(() =>
    this.actions$.pipe(
      ofType(deleteQuickOrderSuccess),
      map(() => loadQuickOrder())
    )
  );

  setToastStickyClass$ = createEffect(
    () =>
      combineLatest([
        this.store.pipe(select(isStickyHeader)),
        this.store.pipe(select(getDeviceType)),
        this.store.pipe(select(getHeaderHeight)),
        this.applyStyle$,
      ]).pipe(
        tap(([sticky, deviceType, headerHeight]) => {
          const container = this.domService.getElementById('toast-container');
          if (container) {
            if (sticky) {
              if (deviceType === 'desktop') {
                container.style.position = 'fixed';
                container.style.top = '30px';
              }
              container.classList.add('custom-toast-sticky');
            } else {
              if (deviceType === 'desktop') {
                container.style.position = 'absolute';
                container.style.top = `${headerHeight - 40}px`;
              }
              container.classList.remove('custom-toast-sticky');
            }
          }
        })
      ),
    { dispatch: false }
  );

  QuickOrderAddtoCartSuccessToast$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(addQuickOrderFlyOutItemsToBasketSuccess, addQuickOrderItemsToBasketSuccess),

        map(
          (): MessagesPayloadType => ({
            message: 'dias.quickorder.add.to.cart.toast.success.message',
          })
        ),
        this.composeQuickOrderToastArguments(),
        map(args => this.toastr.success(...args)),
        tap(() => {
          this.applyStyle$.next();
        })
      ),
    { dispatch: false }
  );

  QuickOrderSuccessToast$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(createQuickOrderFlyoutItemsSuccess),
        map(
          (): MessagesPayloadType => ({
            message: 'dias.quickorder.page.toast.success.message',
          })
        ),
        this.composeQuickOrderToastArguments(),
        map(args => this.toastr.success(...args)),
        tap(() => {
          this.applyStyle$.next();
        })
      ),
    { dispatch: false }
  );

  QuickOrderFailureToast$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          createQuickOrderUploadItemsFail,
          createQuickOrderFlyoutItemsFail,
          addQuickOrderFlyOutItemsToBasketFail,
          addQuickOrderItemsToBasketFail
        ),
        map(
          (): MessagesPayloadType => ({
            message: 'dias.quickorder.page.toast.error.message',
          })
        ),
        this.composeQuickOrderToastArguments(true),
        map(args => this.toastr.error(...args)),
        tap(() => {
          this.applyStyle$.next();
        })
      ),
    { dispatch: false }
  );

  private composeQuickOrderToastArguments(
    //duration?: number,
    disableToastTimeOut?: boolean
  ): OperatorFunction<MessagesPayloadType, [string, string, Partial<IndividualConfig>]> {
    return stream$ =>
      stream$.pipe(
        map(payload => [
          // message translation
          this.translate.instant(payload.message, payload.messageParams),
          // title translation
          payload.title ? this.translate.instant(payload.title, payload.titleParams) : payload.title,
          // extra options
          {
            timeOut: 5000,
            disableTimeOut: disableToastTimeOut,
            progressBar: false,
            closeButton: true,
            enableHtml: true,
            tapToDismiss: false,
            positionClass: 'toast-top-full-width',
          },
        ])
      );
  }
}
